import MailIcon from "@mui/icons-material/Mail";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Badge,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import moment from "moment";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useApiFeedback } from "../../app/useApiFeedback";
import ViewApplication from "../../features/admin/components/Applications/ViewApplication";
import ViewAppTabs from "../../features/admin/components/ViewAppTabs";
import { MarkAsRead, NotificationDto } from "../../features/admin/models";
import { fetchApplicantByIdThunk } from "../../features/applicant/ApplicantSlice";
import CustomModal from "../custom/CustomModal";
import { fetchAllConstantsThunk } from "../dropdowns/Api/constantSlice";
import MessageResponse from "../feedback/messageResponse";
import { selectAdmin } from "../../features/admin/adminSlice";
import { fetchNotificationsThunk, GetDocumentsForApplicantThunk, markNotificationAsReadThunk } from "../../features/admin/thunks";
import { APPLICATION_STAGE } from "../models/enums/applicationStage";
import { BackendApplicationStage } from "../dropdowns/ApplicationStatus";

export default function NotificationsPanel() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const adminAction = useAppSelector(selectAdmin);
  const [data, setData] = React.useState<NotificationDto[]>();
  const [filter, setFilter] = React.useState<APPLICATION_STAGE>(APPLICATION_STAGE.All);
  const dispatch = useAppDispatch();
  const [openDialog, setOpen] = React.useState(false);
  const { feedback, handleApiResponse } = useApiFeedback();
  const [value, setValue] = React.useState(0);
  const [activeModal, setActiveModal] = React.useState<{
    applicationId: number | null;
    tabValue: number;
  }>({ applicationId: null, tabValue: 0 });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    dispatch(fetchNotificationsThunk());
  };
  const handleClose = (e: any, url: string) => {
    setAnchorEl(null);
    if (url && url !== "backdropClick") navigate(`/${url}`);
  };

  // Handle opening modal for a specific application
  const handleOpenModal = (record: NotificationDto) => {
    setActiveModal({
      applicationId: record.applicationId,
      tabValue: 0,
    });

    dispatch(fetchApplicantByIdThunk(record.applicationId)).then(
      (data: any) => {
        handleApiResponse(data, false);
      }
    );
    dispatch(GetDocumentsForApplicantThunk(record.applicationId));
    dispatch(fetchNotificationsThunk());
  };

  React.useEffect(() => {
    if (adminAction.dashboardState.notifications) {
      setData(adminAction.dashboardState.notifications);
    }
  }, [adminAction.dashboardState.notifications]);

  React.useEffect(() => {
    dispatch(fetchAllConstantsThunk());
  }, []);


  return (
    <>
      {data && (
        <>
          <MessageResponse feedback={feedback} />
          <IconButton
            onClick={handleClick}
            sx={{ color: "white", mb: data.length > 0 ? -1.8 : 0, mr: 3.5 }}
          >
            <Badge badgeContent={data.length} color="secondary">
              <MailIcon color="action" sx={{ color: "white" }} />
            </Badge>
          </IconButton>

          {data.length > 0 && (
            <Menu
              id="fade-menu"
              MenuListProps={{
                "aria-labelledby": "fade-button",
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              TransitionComponent={Fade}
            >
              {data.map((notification, key: React.Key) => {
                return (
                  <MenuItem
                    key={key}
                    sx={{
                      py: 1.5,
                      width: "100%",
                      borderBottom:
                        data.length > 1
                          ? "1px solid rgb(207, 206, 206)"
                          : "none",
                    }}
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <Divider orientation="vertical" flexItem />
                      </Grid>
                      <Grid item xs>
                        <Typography fontWeight="medium">
                          {`${notification.applicant.name} ${notification.applicant.surname}`}
                        </Typography>
                        <Typography fontWeight="small" variant="body2">
                          {`${notification.applicant.identityNumber}`}
                        </Typography>
                        <Typography fontWeight="small" variant="body2" mb={2}>
                          {moment(notification.updateDate).fromNow()}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Chip
                          label="Resubmitted"
                          size="small"
                          color="warning"
                          icon={<ReplayIcon fontSize="small" />}
                        />
                      </Grid>
                      <Grid item>
                        <Divider orientation="vertical" flexItem />
                      </Grid>
                      <Grid item>
                        <Tooltip title="View Application">
                          <IconButton onClick={() => handleOpenModal(notification)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <CustomModal
                          maxWidth="lg"
                          open={
                            activeModal.applicationId ===
                            notification.applicationId
                          }
                          onClose={() => {
                            if (notification) {
                              setActiveModal({
                                ...activeModal,
                                applicationId: null,
                              });

                              dispatch(
                                markNotificationAsReadThunk({
                                  notificationId: notification.id,
                                } as MarkAsRead)
                              );
                            }
                          }}
                          title="View Application"
                          dialogTitle={
                            <ViewAppTabs
                              setValue={(newValue) =>
                                setActiveModal({
                                  ...activeModal,
                                  tabValue: newValue as number,
                                })
                              }
                              value={activeModal.tabValue}
                            />
                          }
                        >
                          <ViewApplication
                            applicationId={notification.applicationId}
                            currentApplicationStatus={
                              notification.applicationStage as BackendApplicationStage
                            }
                            setFilterd={setFilter}
                            applicantName={notification.applicant.name}
                            tabValue={activeModal.tabValue}
                          />
                        </CustomModal>
                      </Grid>
                    </Grid>
                  </MenuItem>
                );
              })}
            </Menu>
          )}
        </>
      )}
    </>
  );
}
